import {
  useBillingHistory,
  useConsumerWithBillData,
  useCreditNotes,
  useDownloadBill,
  usePaymentHistory,
  usePaymentReceipt,
} from "@features/cx/consumerManagement/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
import {
  Banknote,
  Calendar,
  Clock,
  Download,
  Eye,
  FileText,
} from "lucide-react";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useViewBill } from "@features/bx/hooks";

interface FinancialTabProps {
  consumerId: string;
  consumerNo: string;
  remoteUtilityId: string;
}

export const FinancialTab: React.FC<FinancialTabProps> = ({
  consumerId,
  consumerNo,
  remoteUtilityId,
}) => {
  // Memoize the filter objects to prevent unnecessary re-renders
  const navigate = useNavigate();
  const viewBill = useViewBill();
  const paymentFilters = useMemo(
    () => ({
      page: DEFAULT_PAGE_NUMBER,
      limit: PAGE_SIZE,
      remote_utility_id: Number(remoteUtilityId),
    }),
    [remoteUtilityId],
  );

  const billingFilters = useMemo(
    () => ({
      page: DEFAULT_PAGE_NUMBER,
      limit: PAGE_SIZE,
      remote_utility_id: Number(remoteUtilityId),
    }),
    [remoteUtilityId],
  );

  const handleViewClick = async (billId: string) => {
    try {
      await viewBill.mutateAsync(billId);
    } catch (error) {
      console.error("View failed:", error);
      // showToast('Failed to view the bill', 'error');
    }
  };

  const {
    data: paymentHistoryData,
    isLoading: paymentsLoading,
    error: paymentsError,
    refetch: refetchPayments,
  } = usePaymentHistory(consumerId, paymentFilters);

  const {
    data: billingHistoryData,
    isLoading: billsLoading,
    error: billsError,
    refetch: refetchBills,
  } = useBillingHistory(consumerId, billingFilters);

  const {
    data: consumerData,
    isLoading: consumerLoading,
    error: consumerError,
  } = useConsumerWithBillData(consumerNo, remoteUtilityId);

  const { data: creditNotesData } = useCreditNotes(consumerId, billingFilters);

  const refetch = React.useCallback(() => {
    refetchPayments();
    refetchBills();
  }, [refetchPayments, refetchBills]);

  const CURRENCY = useCurrency();

  const downloadBill = useDownloadBill();
  const downloadPaymentReceipt = usePaymentReceipt();

  const currentBalance = parseFloat(
    consumerData?.billData?.outstandingBalance || "0",
  );
  const lastPaymentAmount = parseFloat(
    consumerData?.billData?.lastPayAmount || "0",
  );
  const lastPaymentDate = consumerData?.billData?.lastPaymentDate || "";
  const nextPaymentDue = consumerData?.billData?.dueDate || "";

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5" />
              Account Balance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-muted-foreground">
                  Current Balance
                </div>
                <div className="text-3xl font-bold">
                  {CURRENCY} {currentBalance.toFixed(2)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium">Last Payment</div>
                  <div className="text-lg">
                    {CURRENCY}
                    {lastPaymentAmount.toFixed(2)}
                  </div>
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3 mr-1" />
                    {lastPaymentDate}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium">Next Due Date</div>
                  <div className="text-lg">{nextPaymentDue}</div>
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Clock className="h-3 w-3 mr-1" />
                    {nextPaymentDue
                      ? `${nextPaymentDue} days remaining`
                      : "No due date"}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Billing Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium">Rate Plan</div>
                  <div className="text-base">
                    {consumerData?.planDetails?.planName || "N/A"}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium">Billing Cycle</div>
                  <div className="text-base">
                    {consumerData?.billData?.billCycleName || "N/A"}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium">Payment Method</div>
                  <div className="text-base">
                    {consumerData?.billData?.paymentModeDisplay || "N/A"}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium">Billing Method</div>
                  <div className="text-base">
                    {consumerData?.isEBill ? "E-Bill" : "Paper Bill"}
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="text-sm font-medium mb-2">
                  Average Monthly Usage
                </div>
                <div className="flex gap-6">
                  {consumerData?.billData?.consumptionDetails?.map(
                    (detail, index) =>
                      Object.entries(detail).map(([key, value]) => (
                        <div
                          key={key + index}
                          className="flex flex-col items-center"
                        >
                          <span className="text-sm text-muted-foreground">
                            {key}
                          </span>
                          <span className="text-base">
                            {typeof value === "object" && value !== null
                              ? (value as any).consumption
                              : (value as React.ReactNode)}{" "}
                            {key === "Water" ? "m3" : "kWh"}
                          </span>
                        </div>
                      )),
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs
        defaultValue="payments"
        urlMapping={{
          payments: "payments",
          bills: "bills",
        }}
        tabComponents={{
          payments: {
            label: "Payment History",
            component: (
              <Card>
                <CardContent className="pt-6">
                  <GenericTable
                    data={paymentHistoryData?.results || []}
                    columns={[
                      { key: "receiptNo", header: "Receipt No", type: "text" },
                      { key: "paymentDate", header: "Paid On", type: "text" },
                      {
                        key: "paymentPayTypeDisplay",
                        header: "Payment Type",
                        type: "text",
                      },
                      {
                        key: "paymentMode",
                        header: "Payment Mode",
                        type: "text",
                        render: (value) => {
                          return (
                            <span className="capitalize">{value || "N/A"}</span>
                          );
                        },
                      },
                      {
                        key: "amount",
                        header: "Amount",
                        type: "text",
                        render: (value) =>
                          `${CURRENCY}${Number(value || 0).toFixed(2)}`,
                      },
                      {
                        key: "status",
                        header: "Status",
                        type: "badge",
                        badgeConfig: {
                          getVariant: (value) => "default",
                          getClassName: () => "bg-green-100 text-green-800",
                        },
                      },
                    ]}
                    rowKey="id"
                    pagination={{
                      currentPage: DEFAULT_PAGE_NUMBER,
                      totalPages: Math.ceil(
                        (paymentHistoryData?.count || 0) / PAGE_SIZE,
                      ),
                      totalItems: paymentHistoryData?.count || 0,
                      itemsPerPage: PAGE_SIZE,
                      startItem: 1,
                      endItem: Math.min(
                        PAGE_SIZE,
                        paymentHistoryData?.count || 0,
                      ),
                    }}
                    onPageChange={(page) => {
                      // Handle page change
                    }}
                    actions={[
                      {
                        label: "Download Receipt",
                        onClick: (row) => {
                          downloadPaymentReceipt.mutate({
                            paymentId: row.id.toString(),
                            remoteUtilityId: Number(remoteUtilityId),
                          });
                        },
                        icon: <Download className="h-4 w-4" />,
                      },
                    ]}
                    actionsType="icons"
                  />
                </CardContent>
              </Card>
            ),
          },
          bills: {
            label: "Billing History",
            component: (
              <Card>
                <CardContent className="pt-6">
                  <GenericTable
                    data={billingHistoryData?.results || []}
                    columns={[
                      { key: "invoiceNo", header: "Invoice No", type: "text" },
                      {
                        key: "createdDate",
                        header: "Invoice Date",
                        type: "text",
                      },
                      {
                        key: "totalAmountPayable",
                        header: "Amount",
                        type: "text",
                        render: (value) =>
                          `${CURRENCY}${Number(value || 0).toFixed(2)}`,
                      },
                      {
                        key: "outstandingBalance",
                        header: "Pending Ammount",
                        type: "text",
                        render: (value) => `${CURRENCY}${value}`,
                      },
                      {
                        key: "billStatus",
                        header: "Status",
                      },
                      {
                        key: "paymentStatus",
                        header: "Payment Status",
                      },
                    ]}
                    rowKey="id"
                    pagination={{
                      currentPage: DEFAULT_PAGE_NUMBER,
                      totalPages: Math.ceil(
                        (billingHistoryData?.count || 0) / PAGE_SIZE,
                      ),
                      totalItems: billingHistoryData?.count || 0,
                      itemsPerPage: PAGE_SIZE,
                      startItem: 1,
                      endItem: Math.min(
                        PAGE_SIZE,
                        billingHistoryData?.count || 0,
                      ),
                    }}
                    onPageChange={(page) => {
                      // Handle page change
                    }}
                    actions={[
                      {
                        label: "View Details",
                        onClick: (row) => {
                          handleViewClick(row.id.toString());
                        },
                        icon: <Eye className="h-4 w-4" />,
                      },
                      {
                        label: "Download Bill",
                        onClick: (row) => {
                          downloadBill.mutate({
                            billId: row.id.toString(),
                            remoteUtilityId: Number(remoteUtilityId),
                          });
                        },
                        icon: <Download className="h-4 w-4" />,
                      },
                    ]}
                    actionsType="icons"
                  />
                </CardContent>
              </Card>
            ),
          },
          creditNotes: {
            label: "Credit Notes",
            component: (
              <Card>
                <CardContent className="pt-6">
                  <GenericTable
                    data={creditNotesData?.results || []}
                    columns={[
                      {
                        key: "receiptNo",
                        header: "Credit note ID",
                        type: "text",
                      },
                      {
                        key: "amount",
                        header: "Amount",
                        type: "text",
                        render: (value) =>
                          `${CURRENCY}${Number(value || 0).toFixed(2)}`,
                      },
                      {
                        key: "creditNoteReason",
                        header: "Reason",
                        type: "text",
                      },
                      {
                        key: "paymentReceivedStatus",
                        header: "Status",
                        type: "badge",
                        badgeConfig: {
                          getVariant: (value) => "default",
                          getClassName: () => "bg-green-100 text-green-800",
                        },
                      },
                      {
                        key: "createdUserRemoteName",
                        header: "Created By",
                        type: "text",
                      },
                      {
                        key: "createdDate",
                        header: "Created On",
                        type: "text",
                      },
                      {
                        key: "referenceReceiptNo",
                        header: "Linked Entity",
                        type: "text",
                      },
                    ]}
                    rowKey="id"
                    pagination={{
                      currentPage: DEFAULT_PAGE_NUMBER,
                      totalPages: Math.ceil(
                        (creditNotesData?.count || 0) / PAGE_SIZE,
                      ),
                      totalItems: creditNotesData?.count || 0,
                      itemsPerPage: PAGE_SIZE,
                      startItem: 1,
                      endItem: Math.min(PAGE_SIZE, creditNotesData?.count || 0),
                    }}
                    onPageChange={(page) => {
                      // Handle page change
                    }}
                    actions={[
                      {
                        label: "View Details",
                        onClick: (row) => {
                          navigate(`/cx/payments/?tab=creditNotes`);
                        },
                        icon: <Eye className="h-4 w-4" />,
                      },
                    ]}
                    actionsType="icons"
                  />
                </CardContent>
              </Card>
            ),
          },
        }}
        tabsListClassName="grid w-full grid-cols-3"
        idPrefix="financial-tab"
        className="w-full"
        level={1}
      />
    </div>
  );
};
